import {
  ChainOfThoughtStepStatus,
  MessageResponseTypes,
  type ChainOfThoughtStep,
  type GenericItem,
  type HistoryItem,
} from '@carbon/ai-chat';

import type { ServerMessage } from '../types';

// Maps persisted conversation messages onto Carbon AI Chat history items.
//
// Mind the casing: keys Jbuilder writes are camelCase (`toolCalls`,
// `toolResults`), but keys *inside* the `tool_results` jsonb stay snake_case
// (`duration_ms`) because `deep_format_keys` is off. Getting this wrong
// silently drops tool history from a resumed thread.
export function serverMessagesToHistory(
  messages: ServerMessage[]
): HistoryItem[] {
  return messages.map((message) => {
    if (message.role === 'user') {
      return {
        message: {
          id: `srv-${message.id}`,
          input: { text: message.content ?? '' },
        },
        time: message.createdAt,
      };
    }

    const generic: GenericItem[] = [];

    if (message.content) {
      generic.push({
        response_type: MessageResponseTypes.TEXT,
        text: message.content,
      } as GenericItem);
    }

    const resultsById = new Map(
      (message.toolResults || []).map((result) => [result.id, result] as const)
    );

    const steps: ChainOfThoughtStep[] = (message.toolCalls || []).map(
      (call) => {
        const result = resultsById.get(call.id);
        // A call with no stored result predates result persistence; treating it
        // as success matches how the pre-migration transcript rendered it.
        const status = result?.status ?? 'success';
        return {
          title: call.name,
          tool_name: call.name,
          request: { args: call.input },
          response: { content: result?.output ?? null },
          status:
            status === 'success'
              ? ChainOfThoughtStepStatus.SUCCESS
              : status === 'pending'
                ? ChainOfThoughtStepStatus.PROCESSING
                : ChainOfThoughtStepStatus.FAILURE,
          description:
            result?.error ??
            (result?.duration_ms === null || result?.duration_ms === undefined
              ? undefined
              : `Completed in ${result.duration_ms} ms`),
        };
      }
    );

    // Chain-of-thought renders attached to a message's last item, so a turn
    // that only made tool calls needs an item to hang it on or the steps are
    // silently dropped.
    if (steps.length && !generic.length) {
      generic.push({
        response_type: MessageResponseTypes.TEXT,
        text: '',
      } as GenericItem);
    }

    return {
      message: {
        id: `srv-${message.id}`,
        output: { generic },
        ...(steps.length
          ? { message_options: { chain_of_thought: steps } }
          : {}),
      },
      time: message.createdAt,
    };
  });
}
